import React, { useEffect, useRef, useState } from "react";

import {
  isNullOrUndefined,
  isValidNonnegativeNumber,
  useGetFootprintingBeefPrimaryDataById,
  useMutateFootprintingBeefPrimaryData,
  useTranslation,
} from "utils";

import { ENUM_FARM_FOOTPRINTING_BEEF_MOORLAND, FARM_FOOTPRINTING_BEEF_MOORLAND } from "models";

import { Card, Flex, Form, FormInstance, InputNumber, KlimRadioGroup, PageSpin, Typography } from "components";

type BeefMoorlandProps = {
  primaryDataId?: string;
  readOnly?: boolean;
  farmId: string;
  year: number;
};

const BeefMoorlandForm = ({ primaryDataId, readOnly, farmId, year }: BeefMoorlandProps) => {
  const { t } = useTranslation("footprintingBeef");
  const formRef = useRef<FormInstance>(null);

  const [moorlandActive, setMoorlandActive] = useState<ENUM_FARM_FOOTPRINTING_BEEF_MOORLAND | null>(null);
  const [fodderProductionAreaHa, setFodderProductionAreaHa] = useState<number | null>(null);
  const [annualWaterLevelCm, setAnnualWaterLevelCm] = useState<number | null>(null);

  const { data: beefPrimaryData, isLoading, isSuccess } = useGetFootprintingBeefPrimaryDataById(primaryDataId ?? "");
  const { mutate: mutateBeefPrimaryData } = useMutateFootprintingBeefPrimaryData(
    farmId,
    year,
    {
      farmId,
      year,
      moorlandActive: moorlandActive,
      fodderProductionAreaHa: fodderProductionAreaHa,
      annualWaterLevelCm: annualWaterLevelCm,
      isSubmitted: false,
    },
    primaryDataId
  );

  useEffect(
    () => () => {
      if (!readOnly && !isLoading) {
        mutateBeefPrimaryData({});
      }
    },
    [isLoading, mutateBeefPrimaryData, readOnly]
  );

  useEffect(() => {
    if (beefPrimaryData && isSuccess) {
      setMoorlandActive((beefPrimaryData.moorlandActive as ENUM_FARM_FOOTPRINTING_BEEF_MOORLAND) ?? null);
      setFodderProductionAreaHa(beefPrimaryData.fodderProductionAreaHa ?? null);
      setAnnualWaterLevelCm(beefPrimaryData.annualWaterLevelCm ?? null);

      formRef.current?.setFieldsValue({
        fodderProductionAreaHa: beefPrimaryData.fodderProductionAreaHa ?? null,
        annualWaterLevelCm: beefPrimaryData.annualWaterLevelCm ?? null,
      });

      const fieldsToValidate = (["fodderProductionAreaHa", "annualWaterLevelCm"] as const).some(
        (field) => beefPrimaryData[field] !== null && beefPrimaryData[field] !== undefined
      );

      if (fieldsToValidate && formRef.current) {
        formRef.current?.validateFields();
      }
    }
  }, [beefPrimaryData, isSuccess]);

  if (primaryDataId && isLoading) return <PageSpin />;

  return (
    <Card border dataTestId="footprinting-beef-moorland-form" className="padding-200">
      <Form ref={formRef}>
        <Flex vertical gap={16}>
          <Flex align="center" gap={16} justify="space-between">
            <Typography variant="paragraph-m" style={{ maxWidth: "50%" }}>
              {t("beefMoorlandForm.question.moorlandRadioQuestion")}
              {true && <span className="required-asterisk">*</span>}
            </Typography>
            <KlimRadioGroup
              required
              vertical={false}
              disabled={readOnly}
              type="tertiary"
              size="large"
              dataTestId="footprinting-beef-moorland-radio"
              onChange={({ target }) => setMoorlandActive(target.value)}
              value={moorlandActive}
              optionType="button"
              options={[FARM_FOOTPRINTING_BEEF_MOORLAND.YES, FARM_FOOTPRINTING_BEEF_MOORLAND.NO].map((moorland) => ({
                label: t(`beefMoorlandForm.moorlandOptions.${moorland}`),
                value: moorland,
              }))}
            />
          </Flex>

          {moorlandActive === FARM_FOOTPRINTING_BEEF_MOORLAND.YES && (
            <Flex vertical gap={16}>
              <InputNumber
                disabled={readOnly}
                name="fodderProductionAreaHa"
                dataTestId="footprinting-beef-fodder-production-area-input"
                label={t("beefMoorlandForm.question.fodderProductionAreaLabel")}
                suffix={t("beefMoorlandForm.question.fodderProductionAreaSuffix")}
                value={fodderProductionAreaHa}
                initialValue={fodderProductionAreaHa}
                onChange={(v) => setFodderProductionAreaHa(v)}
                guidanceText={t("beefMoorlandForm.notSure")}
                validateFirst
                rules={[
                  { required: true, message: t("beefMoorlandForm.error.validNumber") },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("beefMoorlandForm.error.validNumber")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />

              <InputNumber
                disabled={readOnly}
                name="annualWaterLevelCm"
                dataTestId="footprinting-beef-annual-water-level-input"
                label={t("beefMoorlandForm.question.annualWaterLevelLabel")}
                suffix={t("beefMoorlandForm.question.annualWaterLevelSuffix")}
                value={annualWaterLevelCm}
                initialValue={annualWaterLevelCm}
                onChange={(v) => setAnnualWaterLevelCm(v)}
                guidanceText={t("beefMoorlandForm.notSure")}
                validateFirst
                rules={[
                  { required: true, message: t("beefMoorlandForm.error.validNumber") },
                  { type: "number" },
                  {
                    validator: (_: unknown, v: number) => {
                      if (isNullOrUndefined(v) || !isValidNonnegativeNumber(v)) {
                        return Promise.reject(new Error(t("beefMoorlandForm.error.validNumber")));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              />
            </Flex>
          )}
        </Flex>
      </Form>
    </Card>
  );
};

BeefMoorlandForm.Key = "beef.moorland";
export { BeefMoorlandForm };
